import type { QuoteRequest, QuoteResponse } from "./quote.ts";
import type { SwapRequest, SwapResponse } from "./swap.ts";
import type { TrackRequest, TrackResponse } from "./track.ts";
import { isTerminal } from "./track.ts";
import { SwapKitAPIError } from "./types.ts";

const DEFAULT_BASE_URL = "/swapkit-api";
const DEFAULT_TIMEOUT_MS = 30_000;
const RETRYABLE_HTTP_STATUSES = new Set([429, 500, 502, 503]);

export interface ClientOptions {
  baseUrl?: string;
  timeoutMs?: number;
  debug?: boolean;
}

export class SwapKitClient {
  private baseUrl: string;
  private timeoutMs: number;
  private debug: boolean;

  constructor(opts: ClientOptions = {}) {
    this.baseUrl = opts.baseUrl ?? DEFAULT_BASE_URL;
    this.timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.debug = opts.debug ?? false;
  }

  private async do<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = this.baseUrl + path;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    let bodyStr: string | undefined;
    if (body != null) {
      bodyStr = JSON.stringify(body);
      if (this.debug) {
        console.log(`[swapkit] -> ${method} ${path}\n${bodyStr}`);
      }
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const resp = await fetch(url, {
        method,
        headers,
        body: bodyStr,
        signal: controller.signal,
      });

      const respBody = await resp.text();

      if (this.debug) {
        console.log(
          `[swapkit] <- ${resp.status} ${path}\n${respBody.slice(0, 2000)}`,
        );
      }

      if (resp.status >= 400) {
        const contentType = resp.headers.get("content-type") ?? "";
        if (contentType.includes("text/html")) {
          throw new Error(
            `API error ${resp.status}: blocked by CDN/WAF (check API key)`,
          );
        }
        try {
          const apiErr = JSON.parse(respBody);
          if (apiErr.error) {
            throw new SwapKitAPIError(apiErr);
          }
        } catch (e) {
          if (e instanceof SwapKitAPIError) throw e;
        }
        throw new Error(`API error ${resp.status}: ${respBody.slice(0, 500)}`);
      }

      return JSON.parse(respBody) as T;
    } finally {
      clearTimeout(timer);
    }
  }

  private async doWithRetry<T>(
    method: string,
    path: string,
    body: unknown,
    maxRetries: number,
  ): Promise<T> {
    let lastErr: unknown;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await this.do<T>(method, path, body);
      } catch (err) {
        lastErr = err;
        if (attempt >= maxRetries || !isRetryable(err)) throw err;
        const delayMs = 1000 * 2 ** attempt;
        console.log(
          `[swapkit] ${path} attempt ${attempt + 1}/${maxRetries + 1} failed, retrying in ${delayMs}ms: ${err instanceof Error ? err.message : err}`,
        );
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
    throw lastErr;
  }

  async quote(req: QuoteRequest): Promise<QuoteResponse> {
    return this.doWithRetry<QuoteResponse>("POST", "/v3/quote", req, 3);
  }

  async swap(req: SwapRequest): Promise<SwapResponse> {
    return this.doWithRetry<SwapResponse>("POST", "/v3/swap", req, 2);
  }

  async track(req: TrackRequest): Promise<TrackResponse> {
    return this.do<TrackResponse>("POST", "/track", req);
  }

  async pollTrack(
    req: TrackRequest,
    intervalMs = 10_000,
    timeoutMs = 600_000,
  ): Promise<TrackResponse> {
    const deadline = Date.now() + timeoutMs;
    let lastStatus = "";
    let consecutiveErrors = 0;

    while (Date.now() < deadline) {
      try {
        const resp = await this.track(req);
        consecutiveErrors = 0;

        if (resp.status !== lastStatus) {
          const legSummary = resp.legs?.length
            ? ` (legs: ${resp.legs.map((l) => l.status).join(" -> ")})`
            : "";
          console.log(`[swapkit] track: ${lastStatus || "(init)"} -> ${resp.status}${legSummary}`);
          lastStatus = resp.status;
        }

        if (isTerminal(resp)) return resp;
      } catch (err) {
        consecutiveErrors++;
        if (Date.now() >= deadline) throw err;
        console.log(
          `[swapkit] track error #${consecutiveErrors} (retrying): ${err instanceof Error ? err.message : err}`,
        );
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }

    throw new Error(`tracking timed out in status "${lastStatus}"`);
  }
}

function isRetryable(err: unknown): boolean {
  if (!(err instanceof Error)) return true;
  const msg = err.message;
  for (const code of RETRYABLE_HTTP_STATUSES) {
    if (msg.includes(`API error ${code}`)) return true;
  }
  if (msg.includes("blocked by CDN/WAF")) return true;
  if (err instanceof SwapKitAPIError) return false;
  if (/API error 4\d\d/.test(msg)) return false;
  // Network errors (fetch failures, timeouts) are retryable
  return true;
}
